import { useState, type FormEvent } from 'react';

import { Permission } from '../../api';
import { SIMULATED_CLOCK } from '../../copy';
import { formatDateTime, formatDateTimeForInput } from '../../format';
import { useClearClock, useClock, useSetClock } from './clock';

/**
 * The simulated clock, in the header of every signed-in screen.
 *
 * Three decisions, each of which is the feature rather than a detail of it.
 *
 * **It discovers itself.** Nothing here is behind a build flag. The frontend is
 * one environment-agnostic image, so the question "can this deployment's clock
 * be moved?" is answered by asking the deployment -- see {@link useClock}. In
 * production the answer is a `404` and this component renders nothing at all:
 * no control, no indicator, no empty container.
 *
 * **The indicator is for everybody; the controls are not.** A clock somebody
 * moved is moved for every tenant on the deployment, so a tester reading a
 * cutoff date in the customer portal is reading an invented one just as much as
 * the analyst who invented it. They are told. Moving it is gated on
 * `clock:write`, the same way `filterNavigation` gates a link -- and, as there,
 * this is presentation: the API refuses the call whatever the browser believes.
 *
 * **Nothing is rendered when there is nothing to say.** On a staging deployment
 * with the clock left alone, a principal who cannot move it sees an unchanged
 * header. The warning has to mean something when it appears.
 */
export function SimulatedClock({ permissions }: { permissions: readonly Permission[] }) {
  const clock = useClock();
  const set = useSetClock();
  const clear = useClearClock();
  // `null` means "whatever the clock currently reads". Once somebody types, the
  // draft is theirs and a refetch underneath them may not overwrite it.
  const [draft, setDraft] = useState<string | null>(null);

  const state = clock.data;
  if (state === null || state === undefined || !state.simulation_available) {
    return null;
  }

  const mayMove = permissions.includes(Permission.CLOCK_WRITE);
  if (!state.is_simulated && !mayMove) {
    return null;
  }

  const value = draft ?? formatDateTimeForInput(state.simulated_now);
  const pending = set.isPending || clear.isPending;
  const failed = set.isError || clear.isError;

  function apply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (value === '') {
      return;
    }
    // Reset on success rather than optimistically: if the API refuses, the
    // person keeps what they typed and can correct it.
    set.mutate(value, { onSuccess: () => setDraft(null) });
  }

  return (
    <div
      className={state.is_simulated ? 'simclock simclock--active' : 'simclock'}
      aria-label={SIMULATED_CLOCK.region}
    >
      {state.is_simulated ? (
        // `role="status"` rather than `alert`: it is a standing condition of the
        // deployment, announced once when it appears, not an interruption.
        <p className="simclock__state" role="status">
          <span className="badge simclock__badge">{SIMULATED_CLOCK.badge}</span>
          <span className="simclock__when">
            {SIMULATED_CLOCK.active(formatDateTime(state.simulated_now))}
          </span>
          <span className="simclock__real">
            {SIMULATED_CLOCK.realNow(formatDateTime(state.real_now))}
          </span>
        </p>
      ) : (
        <p className="simclock__state simclock__real">{SIMULATED_CLOCK.inactive}</p>
      )}

      {mayMove ? (
        <form className="simclock__form" onSubmit={apply}>
          {/* The label is hidden rather than absent: the header has no room for
              a caption, and a bare datetime field in a navbar is unidentifiable
              to anyone reading it through a screen reader. */}
          <label className="visually-hidden" htmlFor="simclock-when">
            {SIMULATED_CLOCK.field}
          </label>
          <input
            id="simclock-when"
            className="field__input simclock__input"
            type="datetime-local"
            value={value}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button type="submit" className="button button--primary" disabled={pending}>
            {SIMULATED_CLOCK.set}
          </button>
          {state.is_simulated ? (
            <button
              type="button"
              className="button button--ghost"
              disabled={pending}
              onClick={() => {
                clear.mutate(undefined, { onSuccess: () => setDraft(null) });
              }}
            >
              {SIMULATED_CLOCK.clear}
            </button>
          ) : null}
        </form>
      ) : null}

      {failed ? (
        <p className="simclock__error" role="alert">
          {SIMULATED_CLOCK.failed}
        </p>
      ) : null}
    </div>
  );
}
